/**
 * Login Page Component
 * Requirements: 13.1
 *
 * Provides a login form with email and password fields.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { designTokens } from '@course-authoring/shared-types';

interface LoginPageProps {
  /** Callback when login is successful */
  onLoginSuccess?: () => void;
}

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      onLoginSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Course Authoring</h1>
        <p style={styles.subtitle}>Sign in to continue</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.errorBox} role="alert">
              {error}
            </div>
          )}

          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isLoading}
              style={styles.input}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isLoading}
              style={styles.input}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.button,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={styles.footerText}>
          Have a signup code?{' '}
          <Link to="/signup" style={styles.link}>
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: designTokens.colors.canvasBackground,
    padding: '16px',
  },
  card: {
    backgroundColor: designTokens.colors.inspectorBackground,
    borderRadius: designTokens.borders.blockRadius,
    boxShadow: designTokens.shadows.blockShadow,
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: designTokens.colors.textPrimary,
    textAlign: 'center' as const,
  },
  subtitle: {
    margin: '8px 0 24px',
    fontSize: '14px',
    color: designTokens.colors.textSecondary,
    textAlign: 'center' as const,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: designTokens.colors.textPrimary,
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${designTokens.colors.textMuted}`,
    borderRadius: '6px',
    outline: 'none',
    transition: designTokens.transitions.default,
  },
  errorBox: {
    padding: '12px',
    backgroundColor: designTokens.colors.errorBackground,
    border: `1px solid ${designTokens.colors.errorBorder}`,
    borderRadius: '6px',
    color: designTokens.colors.errorText,
    fontSize: '14px',
  },
  button: {
    padding: '12px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: designTokens.colors.selectionBorder,
    border: 'none',
    borderRadius: '6px',
    marginTop: '8px',
    transition: designTokens.transitions.default,
  },
  footerText: {
    marginTop: '20px',
    fontSize: '13px',
    color: designTokens.colors.textSecondary,
    textAlign: 'center' as const,
  },
  link: {
    color: '#4183C4',
    fontWeight: 'normal',
    textDecoration: 'underline',
  },
};
